import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Handshake, ShieldCheck, MapPin, Users, Instagram, Facebook,
  Globe, Phone, Sparkles, Share2,
} from 'lucide-react';
import { api } from '../services/api';
import Breadcrumbs from '../components/Breadcrumbs';
import ColetivoCalendar from '../components/ColetivoCalendar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { resolveBackendImage } from '../utils/brandLogo';

const TIPO_BADGE = {
  feira: 'bg-amber-400 text-amber-950',
  cooperativa: 'bg-green-400 text-green-950',
  associacao: 'bg-blue-400 text-blue-950',
  grupo: 'bg-purple-400 text-purple-950',
  rede: 'bg-pink-400 text-pink-950',
};
const TIPO_LABEL = {
  feira: 'Feira', cooperativa: 'Cooperativa', associacao: 'Associação',
  grupo: 'Grupo', rede: 'Rede',
};

export default function ColetivoPaginaPublica() {
  const { slug } = useParams();
  const { user } = useAuth() || {};
  const [coletivo, setColetivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myBiz, setMyBiz] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/v2/coletivos/${slug}/publico`)
      .then((rc) => setColetivo(rc.data))
      .catch(() => setColetivo(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user) return;
    api.get('/v2/businesses/me/businesses').then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setMyBiz(list);
    }).catch(() => setMyBiz([]));
  }, [user]);

  const myBizInColetivo = (coletivo?.membros_aprovados || []).some((m) => myBiz.find((b) => b.id === m.id));

  async function solicitarAdesao(negocioId) {
    setSubmitting(true);
    try {
      const r = await api.post(`/v2/coletivos/${coletivo.id}/aderir`, { negocio_id: negocioId });
      toast.success(r.data?.message || 'Solicitação enviada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao solicitar adesão');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando coletivo…</div>;
  if (!coletivo) {
    return (
      <div className="p-12 text-center">
        <h2 className="text-xl font-bold">Coletivo não encontrado</h2>
        <Link to="/coletivos" className="text-purple-600 underline mt-2 inline-block">Ver todos os coletivos</Link>
      </div>
    );
  }

  const cor = '#7C3AED';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="coletivo-pagina-publica">
      {/* Banner */}
      <div className="relative h-48 md:h-64 bg-gradient-to-br from-purple-700 to-pink-600 overflow-hidden">
        {coletivo.foto_capa && (
          <img src={resolveBackendImage(coletivo.foto_capa)} alt={coletivo.nome} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      </div>

      {/* Identidade flutuante */}
      <div className="max-w-5xl mx-auto px-4 -mt-16 relative">
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-purple-100 shrink-0 border-4 border-white shadow-md -mt-12">
              {coletivo.foto_perfil ? (
                <img src={resolveBackendImage(coletivo.foto_perfil)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-purple-700">
                  <Handshake className="w-10 h-10" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Breadcrumbs items={[{ label: 'Coletivos', path: '/coletivos' }, { label: coletivo.nome }]} className="mb-2" />
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TIPO_BADGE[coletivo.tipo] || TIPO_BADGE.grupo}`}>
                  {TIPO_LABEL[coletivo.tipo] || coletivo.tipo}
                </span>
                {coletivo.verificado && (
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-bold">
                    <ShieldCheck className="w-3 h-3" /> Verificado
                  </span>
                )}
                {coletivo.featured && (
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-xs font-bold">
                    <Sparkles className="w-3 h-3" /> Destaque
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold leading-tight" data-testid="coletivo-nome">{coletivo.nome}</h1>
              {coletivo.descricao && <p className="text-sm text-gray-600 mt-1">{coletivo.descricao}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                {coletivo.localizacao?.bairro && (
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <MapPin className="w-4 h-4" /> {coletivo.localizacao.bairro}
                    {coletivo.localizacao.referencia && ` · ${coletivo.localizacao.referencia}`}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <Users className="w-4 h-4" /> <strong>{coletivo.total_membros_aprovados || 0}</strong> membros
                </span>
              </div>
              {/* Redes */}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {coletivo.redes_sociais?.instagram && (
                  <a href={coletivo.redes_sociais.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-50 text-pink-700 text-xs hover:bg-pink-100">
                    <Instagram className="w-3 h-3" /> Instagram
                  </a>
                )}
                {coletivo.redes_sociais?.facebook && (
                  <a href={coletivo.redes_sociais.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs hover:bg-blue-100">
                    <Facebook className="w-3 h-3" /> Facebook
                  </a>
                )}
                {coletivo.redes_sociais?.whatsapp && (
                  <a href={`https://wa.me/${coletivo.redes_sociais.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs hover:bg-green-100">
                    <Phone className="w-3 h-3" /> WhatsApp
                  </a>
                )}
                {coletivo.redes_sociais?.website && (
                  <a href={coletivo.redes_sociais.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200">
                    <Globe className="w-3 h-3" /> Site
                  </a>
                )}
                {coletivo.localizacao?.coordenadas?.coordinates && (
                  <a
                    href={`https://www.google.com/maps?q=${coletivo.localizacao.coordenadas.coordinates[1]},${coletivo.localizacao.coordenadas.coordinates[0]}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-xs hover:bg-purple-100"
                  >
                    <MapPin className="w-3 h-3" /> Como chegar
                  </a>
                )}
                <button
                  onClick={() => {
                    if (navigator.share) navigator.share({ title: coletivo.nome, url: window.location.href }).catch(() => {});
                    else { navigator.clipboard.writeText(window.location.href); toast.success('Link copiado'); }
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200"
                >
                  <Share2 className="w-3 h-3" /> Compartilhar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      {coletivo.sobre && (
        <section className="max-w-5xl mx-auto px-4 mt-6">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <h2 className="text-lg font-bold mb-3">Sobre</h2>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{coletivo.sobre}</p>
            {coletivo.responsavel_nome && (
              <div className="mt-4 pt-3 border-t text-xs text-gray-500">
                <strong>Responsável:</strong> {coletivo.responsavel_nome}
                {coletivo.responsavel_contato && ` · ${coletivo.responsavel_contato}`}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Adesão */}
      {coletivo.adesao_aberta && user && myBiz.length > 0 && !myBizInColetivo && (
        <section className="max-w-5xl mx-auto px-4 mt-6">
          <div className="rounded-xl p-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="coletivo-cta-adesao">
            <h3 className="text-lg font-bold mb-2">Quer fazer parte deste coletivo?</h3>
            <p className="text-sm text-white/90 mb-4">
              {coletivo.adesao_automatica
                ? 'Aprovação automática — você passa a fazer parte imediatamente.'
                : 'Sua solicitação será revisada pelo responsável.'}
            </p>
            <div className="flex flex-wrap gap-2">
              {myBiz.map((b) => (
                <button
                  key={b.id}
                  onClick={() => solicitarAdesao(b.id)}
                  disabled={submitting}
                  data-testid={`coletivo-aderir-${b.id}`}
                  className="px-4 py-2 rounded-lg bg-white text-purple-700 text-sm font-medium hover:bg-purple-50 disabled:opacity-50 transition"
                >
                  Solicitar com {b.nome}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Membros */}
      <section className="max-w-5xl mx-auto px-4 mt-6">
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Quem faz parte
            <span className="text-sm font-normal text-gray-500">({coletivo.total_membros_aprovados || 0})</span>
          </h2>
          {coletivo.membros_aprovados?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {coletivo.membros_aprovados.map((m) => {
                const path = m.municipio_slug && m.categoria_slug && m.slug
                  ? `/${m.municipio_slug}/${m.categoria_slug}/${m.slug}`
                  : `/negocio/${m.slug || m.id}`;
                return (
                  <Link
                    key={m.id}
                    to={path}
                    data-testid={`coletivo-membro-${m.id}`}
                    className="group flex items-center gap-2 p-2 rounded-lg hover:bg-purple-50 transition"
                  >
                    <img
                      src={resolveBackendImage(m.avatar_url)}
                      alt={m.nome}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-purple-200 group-hover:ring-purple-400 transition"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate group-hover:text-purple-700">{m.nome}</div>
                      <div className="text-xs text-gray-500 truncate">{m.categoria}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Ainda não há membros aprovados.</p>
          )}
        </div>
      </section>

      {/* Agenda / Calendário Compartilhado */}
      <section className="max-w-5xl mx-auto px-4 mt-6 mb-12">
        <ColetivoCalendar slug={slug} color={cor} />
      </section>

    </div>
  );
}
